import { Combobox as HeadlessCombobox, Transition } from "@headlessui/react";
import {
  Fragment,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
  useState,
} from "react";
import classNames from "classnames";
import { ChevronDownIcon, SearchIcon } from "@talladega/icons";
import { Status } from "@talladega/types";
import { useTranslation } from "next-i18next";
import { Feedback, getInputFocusStyle, Hint } from "../index";

type Props<T> = {
  items: T[];
  /**
   * Unique key `Option` prop
   */
  keyOption: (item: T) => string | number;

  /**
   * Option's display value
   */
  displayOption: (item?: T) => string | number | boolean | ReactNode;

  /**
   * Option's selected value
   */
  valueOption: (item: T) => T | string | number | boolean;

  /**
   * Option's disable prop
   */
  disabledOption?: (item: T) => boolean;

  /**
   * Custom render for Option
   */
  renderOption?: (
    item: T,
    selected?: boolean,
    active?: boolean,
    disabled?: boolean
  ) => JSX.Element;

  label?: string;
  placeholder?: string;
  hint?: string;
  name?: string;
  disabled?: boolean;
  dropdownIcon?: ReactNode;
  status?: Status;
  feedback?: ReactNode;
  hideDropdownBtn?: boolean;
  onInputChange?: (value: string) => void;
  onInputClick?: () => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  itemsFilter?: "nameIncludesQuery" | "none";
  onItemsFilter?: (item: T, query: string) => boolean;
  itemsTextDisplay?: "truncate" | "full";
  inputRef?: Ref<HTMLInputElement>;
  inputTextSize?: "sm" | "md";
  showClearButton?: boolean;
  onClear?: () => void;
  openOnClick?: boolean;
  groupBy?: keyof T;
  groupByValue?: keyof T;
  showAsInput?: boolean;
  buttonIcon?: ReactNode;
  dropDownClassName?: string;
};

type CheckboxListDropdownProps<T> = Omit<Props<T>, "name" | "feedback"> & {
  open: boolean;
  displayValue: () => string;
  multiple?: boolean;
  onChange?: (selected: T[]) => void;
  selectedValue?: T[] | T;
};

type MultiCheckboxListProps<T> = {
  multiple: true;
  onChange: (selected: T[]) => void;
  selectedValue?: T[];
  displayButton: (items: T[]) => string | number | boolean;
} & Props<T>;

type CheckboxListProps<T> = {
  multiple?: false;
  onChange: (selected: T) => void;
  selectedValue?: T;
  displayButton: (items: T) => string | number | boolean;
} & Props<T>;

const CheckboxListDropdown = <T,>({
  open,
  items,
  keyOption,
  displayOption,
  valueOption,
  disabledOption,
  renderOption,
  label,
  placeholder,
  status = "default",
  onInputChange,
  itemsFilter = "nameIncludesQuery",
  onItemsFilter,
  itemsTextDisplay = "truncate",
  inputRef,
  inputTextSize = "md",
  hint,
  multiple,
  onChange,
  selectedValue,
  groupBy,
  groupByValue,
  displayValue,
  showAsInput = false,
  disabled = false,
  openOnClick,
  hideDropdownBtn,
  dropdownIcon,
  buttonIcon,
  dropDownClassName,
}: CheckboxListDropdownProps<T>) => {
  if (!showAsInput && buttonIcon === undefined) {
    throw new Error(
      "The `buttonIcon` prop is required when `showAsInput` is false."
    );
  }

  if (showAsInput && label === undefined) {
    throw new Error("The `label` prop is required when `showAsInput` is true.");
  }

  const { t } = useTranslation(["common-components"]);
  const [query, setQuery] = useState("");
  const filteredItems: T[] = useMemo(() => {
    if (!query) {
      return items;
    }
    if (onItemsFilter) {
      return items.filter((item) => onItemsFilter(item, query));
    }
    if (itemsFilter === "none") {
      return items;
    }

    // Default filter logic for 'nameIncludesQuery'
    const searchTerm = query
      .split(",")
      .pop()
      ?.trim()
      .toLowerCase()
      .replace(/\s+/g, "");

    if (!searchTerm) {
      return items;
    }

    return items.filter((item) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const name = (item as any)?.name;
      if (typeof name === "string") {
        return name.toLowerCase().replace(/\s+/g, "").includes(searchTerm);
      }
      return false;
    });
  }, [items, onItemsFilter, query, itemsFilter]);

  const allFilteredSelected = useMemo(() => {
    if (!multiple || !Array.isArray(selectedValue) || !filteredItems.length) {
      return false;
    }
    const selectedKeys = new Set(selectedValue.map(keyOption));
    return filteredItems.every((item) => selectedKeys.has(keyOption(item)));
  }, [filteredItems, keyOption, multiple, selectedValue]);

  const handleSelectAll = useCallback(() => {
    if (!multiple || !onChange || !Array.isArray(selectedValue)) return;

    const onChangeMultiple = onChange as (selected: T[]) => void;
    const filteredKeys = new Set(filteredItems.map(keyOption));

    if (allFilteredSelected) {
      // Deselect all visible items
      const newSelection = selectedValue.filter(
        (item) => !filteredKeys.has(keyOption(item))
      );
      onChangeMultiple(newSelection);
    } else {
      // Select all visible items, avoiding duplicates
      const currentSelection = [...selectedValue];
      const selectedKeys = new Set(selectedValue.map(keyOption));

      filteredItems.forEach((item) => {
        if (!selectedKeys.has(keyOption(item))) {
          currentSelection.push(item);
        }
      });
      onChangeMultiple(currentSelection);
    }
  }, [
    allFilteredSelected,
    filteredItems,
    keyOption,
    multiple,
    onChange,
    selectedValue,
  ]);

  const statusColor: Record<string, string> = {
    default: "border-primary kijiji:border-other-light-100",
    error: "border-error focus:border-error focus:shadow-error/8",
  };

  const searchIconClassName = classNames(
    "pl-1 text-md font-medium bg-white w-full border-none focus:border-none focus:outline-none focus:ring-0",
    {
      "text-xs sm:text-sm h-10 font-medium": inputTextSize === "sm",
    },
    statusColor[status] || statusColor.default
  );

  const mainInputClassName = classNames(
    "text-md form-input font-medium bg-white border rounded-lg w-full focus:outline-none focus:shadow-input placeholder-gray-300",
    getInputFocusStyle(),
    {
      "bg-background-light-300 disabled:border-other-light-100": disabled,
    },
    {
      "pr-8": !hideDropdownBtn,
    },
    {
      "text-xs sm:text-sm h-10 font-medium": inputTextSize === "sm",
    },
    statusColor[status] || statusColor.default
  );

  const mainComboboxInput = () => (
    <HeadlessCombobox.Input
      className={mainInputClassName}
      onChange={(event) => {
        setQuery(event.target.value);
        if (onInputChange) {
          onInputChange(event.target.value);
        }
      }}
      displayValue={displayValue}
      placeholder={placeholder}
      ref={inputRef}
    />
  );

  const defaultRenderOption = (
    item: T,
    selected?: boolean,
    isGroupByItem = false
  ) => {
    return (
      <div className="flex items-center">
        <input
          type="checkbox"
          readOnly
          checked={!!selected}
          className="mr-3 h-4 w-4 rounded border-gray-300 text-secondary-500 focus:ring-secondary-500 pointer-events-none"
        />
        <span
          className={classNames("block flex-grow", {
            "font-medium": selected && !isGroupByItem,
            "font-normal": !selected && !isGroupByItem,
            "font-semibold": isGroupByItem,
            truncate: itemsTextDisplay === "truncate",
            "pl-3": groupBy,
          })}
        >
          {displayOption(item)}
        </span>
      </div>
    );
  };

  const comboboxOption = (item: T, isGroupByItem = false) => (
    <HeadlessCombobox.Option
      key={keyOption(item)}
      // eslint-disable-next-line @typescript-eslint/no-shadow
      className={({ active, disabled }) =>
        classNames(
          "select-none relative",
          {
            "py-2 px-3": !isGroupByItem,
          },
          {
            "py-1": isGroupByItem,
          },
          {
            "bg-background-light-300 text-gray-900": active,
          },
          {
            "text-gray-900": !active,
          },
          {
            "cursor-pointer": !disabled,
          },
          {
            "cursor-default text-gray-100": disabled,
          }
        )
      }
      disabled={disabledOption ? disabledOption(item) : false}
      value={valueOption(item)}
    >
      {({ selected }) =>
        renderOption
          ? renderOption(item, selected)
          : defaultRenderOption(item, selected, isGroupByItem)
      }
    </HeadlessCombobox.Option>
  );

  return (
    <>
      {showAsInput && label && (
        <div className="flex flex-row justify-between">
          <HeadlessCombobox.Label className="text-sm font-medium text-secondary">
            {label}
          </HeadlessCombobox.Label>
        </div>
      )}
      <div className="relative py-1">
        {showAsInput ? (
          <>
            {openOnClick ? (
              <HeadlessCombobox.Button className="w-full">
                {mainComboboxInput()}
              </HeadlessCombobox.Button>
            ) : (
              mainComboboxInput()
            )}
            {!hideDropdownBtn && (
              <HeadlessCombobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-5 focus:outline-none">
                <span className="absolute inset-y-2 right-0 flex items-center h-8 w-8 text-text-light-200 pointer-events-none">
                  {dropdownIcon || <ChevronDownIcon />}
                </span>
              </HeadlessCombobox.Button>
            )}
          </>
        ) : (
          <HeadlessCombobox.Button className="w-full flex items-center">
            {buttonIcon}
          </HeadlessCombobox.Button>
        )}

        <Transition
          show={open}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <div
            className={classNames(
              "absolute right-0 z-10 min-w-44 bg-white",
              dropDownClassName
            )}
          >
            {!showAsInput && (
              <div className="flex items-center justify-center px-3 gap-0">
                <SearchIcon className="w-5 h-5 text-gray-400" />
                <HeadlessCombobox.Input
                  className={searchIconClassName}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    if (onInputChange) {
                      onInputChange(event.target.value);
                    }
                  }}
                  placeholder={placeholder}
                  ref={inputRef}
                />
              </div>
            )}
            <HeadlessCombobox.Options
              className={classNames(
                "bg-white mt-1 shadow-elevation-03 max-h-60 py-1 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm overflow-auto rounded-md",
                { "px-1": groupByValue }
              )}
            >
              {multiple && filteredItems.length > 0 && (
                <div
                  role="option"
                  aria-selected={allFilteredSelected}
                  className="select-none relative py-2 px-3 cursor-pointer hover:bg-background-light-300 border-b-2"
                  onClick={handleSelectAll}
                  onKeyDown={handleSelectAll}
                  tabIndex={0}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      readOnly
                      checked={allFilteredSelected}
                      className="mr-3 h-4 w-4 rounded border-gray-300 text-secondary-500 focus:ring-secondary-500 pointer-events-none"
                    />
                    <span className="block flex-grow font-medium">
                      {t("common-components:all_columns")}
                    </span>
                  </div>
                </div>
              )}
              {filteredItems.map((item, i) => {
                const currentItem = item as T;
                if (groupBy && groupBy in (item as Record<string, unknown>)) {
                  const currentGroupLabel = currentItem[groupBy];
                  const showGroupLabel =
                    i === 0 ||
                    currentGroupLabel !== (filteredItems[i - 1] as T)[groupBy];

                  return (
                    <div
                      key={keyOption(currentItem)}
                      className={classNames({
                        "border-t border-gray-50 pt-3":
                          showGroupLabel && i !== 0,
                        "pt-2": i === 0,
                      })}
                    >
                      {showGroupLabel &&
                        (groupByValue ? (
                          comboboxOption(currentItem[groupByValue] as T, true)
                        ) : (
                          <span className="text-sm font-semibold pl-3">{`${currentGroupLabel}`}</span>
                        ))}
                      {comboboxOption(currentItem)}
                    </div>
                  );
                }
                return comboboxOption(currentItem);
              })}
            </HeadlessCombobox.Options>
          </div>
        </Transition>
        <div>{hint && <Hint hint={hint} align="left" />}</div>
      </div>
    </>
  );
};

const CheckboxList = <T,>({
  onChange,
  items,
  selectedValue,
  keyOption,
  displayOption,
  displayButton,
  valueOption,
  disabledOption,
  renderOption,
  label,
  placeholder,
  hint,
  name = "",
  disabled = false,
  dropdownIcon,
  status = "default",
  feedback = "",
  onInputChange,
  onInputClick,
  onInputFocus,
  onInputBlur,
  hideDropdownBtn = false,
  itemsFilter = "nameIncludesQuery",
  onItemsFilter,
  itemsTextDisplay = "truncate",
  inputRef,
  inputTextSize = "md",
  showClearButton = false,
  onClear,
  multiple,
  openOnClick,
  groupBy,
  groupByValue,
  showAsInput,
  buttonIcon,
  dropDownClassName,
}: MultiCheckboxListProps<T> | CheckboxListProps<T>) => {
  const commonProps = {
    items,
    keyOption,
    displayOption,
    valueOption,
    disabledOption,
    renderOption,
    label,
    placeholder,
    hint,
    disabled,
    dropdownIcon,
    status,
    onInputChange,
    onInputClick,
    onInputFocus,
    onInputBlur,
    hideDropdownBtn,
    itemsFilter,
    onItemsFilter,
    itemsTextDisplay,
    inputRef,
    inputTextSize,
    showClearButton,
    onClear,
    openOnClick,
    groupBy,
    groupByValue,
    showAsInput,
    buttonIcon,
    dropDownClassName,
  };

  return (
    <div>
      {multiple ? (
        <HeadlessCombobox
          value={selectedValue}
          onChange={onChange}
          multiple
          disabled={disabled}
          name={name}
        >
          {({ open }) => (
            <CheckboxListDropdown
              open={open}
              displayValue={() => {
                return displayButton(selectedValue as T[]) as string;
              }}
              multiple={multiple}
              onChange={onChange}
              selectedValue={selectedValue}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...commonProps}
            />
          )}
        </HeadlessCombobox>
      ) : (
        <HeadlessCombobox
          value={selectedValue}
          onChange={onChange}
          disabled={disabled}
          name={name}
        >
          {({ open }) => (
            <CheckboxListDropdown
              open={open}
              displayValue={() => {
                return displayButton(selectedValue as T) as string;
              }}
              multiple={multiple}
              selectedValue={selectedValue}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...commonProps}
            />
          )}
        </HeadlessCombobox>
      )}

      {feedback && <Feedback status={status}>{feedback}</Feedback>}
    </div>
  );
};

export default CheckboxList;
